import type { CashSessionRow } from '../../cash/infra-api';
import { todayIsoDate } from '../infra/expenseListSql';
import { formatExpenseDate } from '../domain/expenseLabels';
import { expenseErpCopy } from '../domain/expenseErpCopy';
import type { ExpenseCreatePaymentIntent, ExpenseSettleMethod } from './expenseSettlementMethods';

export type ExpensePaymentSubmitInput = {
  paymentStatus: ExpensePaymentStatusChoice;
  paidForm: ExpensePaidFormChoice | null;
  alreadyPaidKind: ExpenseAlreadyPaidKind | null;
  settleMethod: ExpenseSettleMethod;
  activeCashSessionId: string | null;
  settleNote: string;
  pastCashDrawerConfirmed: boolean;
  expenseDate: string;
  amount?: number;
  residual?: number;
  checkId?: string | null;
};

/** Días sin coincidir con hoy antes de pedir confirmación extra en efectivo de caja. */
export const PAST_EXPENSE_CASH_DRAWER_CONFIRM_DAYS = 7;

export type ExpensePaymentStatusChoice = 'unpaid' | 'paid';

export type ExpensePaidFormChoice =
  | 'cash_drawer_now'
  | 'transfer'
  | 'card'
  | 'check'
  | 'already_paid';

export type ExpenseAlreadyPaidKind = 'cash_late' | 'non_drawer';

export type ActiveCashSessionInfo = {
  session: CashSessionRow;
  operatorLabel: string | null;
};

export function expenseDateIsToday(expenseDate: string, today = todayIsoDate()): boolean {
  return expenseDate.trim().slice(0, 10) === today.slice(0, 10);
}

export function expenseDateAgeDays(expenseDate: string, today = todayIsoDate()): number {
  const expense = new Date(`${expenseDate.slice(0, 10)}T12:00:00`);
  const ref = new Date(`${today.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(expense.getTime()) || Number.isNaN(ref.getTime())) return 0;
  return Math.floor((ref.getTime() - expense.getTime()) / 86400000);
}

export function needsPastExpenseCashConfirm(expenseDate: string, today = todayIsoDate()): boolean {
  if (expenseDateIsToday(expenseDate, today)) return false;
  return expenseDateAgeDays(expenseDate, today) > PAST_EXPENSE_CASH_DRAWER_CONFIRM_DAYS;
}

export function buildLateCashPaymentNoteTemplate(expenseDate: string): string {
  const label = formatExpenseDate(expenseDate);
  return `Pagado en efectivo de la caja el ${label}, no registrado a tiempo.`;
}

export function resolveSettleMethodFromPaymentForm(input: {
  paidForm: ExpensePaidFormChoice;
  alreadyPaidKind?: ExpenseAlreadyPaidKind | null;
  nonDrawerMethod?: ExpenseSettleMethod;
}): ExpenseSettleMethod {
  if (input.paidForm === 'cash_drawer_now') return 'cash_drawer';
  if (input.paidForm === 'transfer') return 'transfer';
  if (input.paidForm === 'card') return 'card';
  if (input.paidForm === 'check') return 'check';
  if (input.paidForm === 'already_paid') {
    if (input.alreadyPaidKind === 'cash_late') return 'other';
    return input.nonDrawerMethod ?? 'other';
  }
  return 'other';
}

export function isLateCashPaymentNoteRequired(input: {
  paymentStatus: ExpensePaymentStatusChoice;
  paidForm: ExpensePaidFormChoice | null;
  alreadyPaidKind: ExpenseAlreadyPaidKind | null;
}): boolean {
  return (
    input.paymentStatus === 'paid' &&
    input.paidForm === 'already_paid' &&
    input.alreadyPaidKind === 'cash_late'
  );
}

export function getExpensePaymentSubmitBlockReason(input: ExpensePaymentSubmitInput): string | null {
  const block = expenseErpCopy.paymentSubmitBlock;
  if (input.paymentStatus === 'unpaid') return null;
  if (!input.paidForm) return block.choosePaidForm;
  if (input.settleMethod === 'check' && !input.checkId) return block.chooseCheck;
  if (input.paidForm === 'already_paid' && !input.alreadyPaidKind) return block.chooseAlreadyPaidKind;
  if (input.settleMethod === 'cash_drawer' && !input.activeCashSessionId) return block.openCashSession;
  if (isLateCashPaymentNoteRequired(input) && !input.settleNote.trim()) return block.needPaymentNote;
  if (input.settleMethod === 'other' && !input.settleNote.trim()) return block.needPaymentNote;
  if (
    input.paidForm === 'cash_drawer_now' &&
    needsPastExpenseCashConfirm(input.expenseDate) &&
    !input.pastCashDrawerConfirmed
  ) {
    return block.confirmPastCashDrawer;
  }
  if (input.amount != null) {
    if (!Number.isFinite(input.amount) || input.amount <= 0) return block.invalidAmount;
    if (input.residual != null && input.amount > input.residual) return block.amountOverResidual;
  }
  return null;
}

export function canSubmitExpensePayment(input: ExpensePaymentSubmitInput): boolean {
  if (input.paymentStatus === 'unpaid') return true;
  return getExpensePaymentSubmitBlockReason(input) === null;
}

export function isoDateToPaidAt(isoDate: string): string {
  const normalized = isoDate.trim().slice(0, 10);
  return `${normalized}T12:00:00.000Z`;
}

export function defaultPaidDateForForm(expenseDate: string): string {
  return expenseDateIsToday(expenseDate) ? todayIsoDate() : expenseDate.slice(0, 10);
}
